import { DateTime } from "luxon";
import Card from "../shared/Card/Card";
import useJobAssemblyTimers from "../../hooks/useJobAssemblyTimers";
import formatDateTime from "../../utils/formatDateTime";

interface JobDismantlingTimerCardProps {
  jobId?: string;
  /** Render the card as a collapsible panel. */
  collapsible?: boolean;
  /** When collapsible, whether it starts expanded. Defaults to expanded. */
  defaultOpen?: boolean;
}

export default function JobDismantlingTimerCard({
  jobId,
  collapsible = false,
  defaultOpen = true,
}: JobDismantlingTimerCardProps) {
  const {
    totalElapsedSeconds,
    hasActiveSession,
    firstStartedAt,
    lastActivityAt,
  } = useJobAssemblyTimers(jobId);

  const formatDuration = (seconds: number) => {
    const safeSeconds = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(safeSeconds / 3600).toString().padStart(2, "0");
    const minutes = Math.floor((safeSeconds % 3600) / 60).toString().padStart(2, "0");
    const remainingSeconds = Math.floor(safeSeconds % 60).toString().padStart(2, "0");

    return `${hours}:${minutes}:${remainingSeconds}`;
  };

  return (
    <Card title="Dismantling Timer" subtitle="Start, pause and finish each assembly timer. Totals include active sessions." bodyClassName="p-4" collapsible={collapsible} defaultOpen={defaultOpen}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="text-left">
          <p className="font-mono text-5xl font-semibold tracking-tight text-primary-600 lg:text-6xl">
            {formatDuration(totalElapsedSeconds)}
          </p>
          <p className="mt-1 text-xs font-medium uppercase tracking-wide text-gray-500">
            {hasActiveSession ? "Session active" : "Not started"}
          </p>
        </div>

        <div className="flex w-full flex-col gap-3 lg:w-80">
          <div className="rounded-md bg-white px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Started</p>
            <p className="text-sm text-gray-700">
              {firstStartedAt
                ? formatDateTime(firstStartedAt, DateTime.DATETIME_SHORT)
                : "-"}
            </p>
          </div>
          <div className="rounded-md bg-white px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Last Activity</p>
            <p className="text-sm text-gray-700">
              {lastActivityAt
                ? formatDateTime(lastActivityAt, DateTime.DATETIME_SHORT)
                : "-"}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
